'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';

type ServiceStat = {
  label: string;
  value: string;
  helper?: string;
};

type ServiceFeature = {
  title: string;
  description: string;
};

export type ServicePageTemplateProps = {
  title: string;
  heroTagline: string;
  description: string;
  accent: string; // The main "Fun Color"
  surface: string; // The light gradient for the glass card
  stats: ServiceStat[];
  features: ServiceFeature[];
  funElement?: ReactNode;
  ctaLine?: string;
  ctaServiceKey?: string;
};

export function ServicePageTemplate({
  title,
  heroTagline,
  description,
  accent,
  surface,
  stats,
  features,
  funElement,
  ctaLine,
  ctaServiceKey,
}: ServicePageTemplateProps) {
  const ctaHref = ctaServiceKey
    ? `/home-pros/lead?service=${encodeURIComponent(ctaServiceKey)}`
    : '/home-pros/lead';

  return (
    <div className="relative min-h-screen overflow-x-hidden font-sans selection:bg-slate-200 selection:text-slate-900">
      
      {/* --- 1. GLOBAL ATMOSPHERE (Dynamic based on Accent) --- */}
      <div className="fixed inset-0 -z-50 overflow-hidden bg-[#F9F9F7]">
        {/* Grain Texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")` }} 
        />
        
        {/* Dynamic Accent Orb (Top Left) */}
        <div 
          className="absolute top-[-20%] left-[-10%] h-[70vw] w-[70vw] rounded-full mix-blend-multiply blur-[120px] opacity-25"
          style={{ background: accent }} 
        />
        
        {/* Secondary Orb (Bottom Right - Lighter version) */}
        <div 
          className="absolute bottom-[-20%] right-[-10%] h-[80vw] w-[80vw] rounded-full mix-blend-multiply blur-[100px] opacity-20"
          style={{ background: surface }} 
        />
      </div>

      {/* --- 2. HEADER --- */}
      <header className="sticky top-0 z-50 border-b border-slate-200/50 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-3 px-4 py-3 sm:justify-between sm:px-6 sm:py-4">
          <Link 
            href="/home-pros" 
            className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/50 px-4 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-slate-500 transition-all hover:bg-white hover:text-slate-800 hover:shadow-sm sm:text-[0.65rem] sm:tracking-[0.2em]"
          >
            <span>← Back</span>
          </Link>
          
          <div className="flex max-w-full items-center justify-center gap-2 text-center">
            <span className="h-2 w-2 rounded-full shadow-sm" style={{ backgroundColor: accent }} />
            <span className="break-words text-[0.6rem] font-bold uppercase tracking-[0.2em] text-slate-400 sm:text-[0.65rem] sm:tracking-[0.25em]">
              {title}
            </span>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        
        {/* --- HERO CARD --- */}
        <section
          className="relative overflow-hidden rounded-[28px] border border-white/60 p-5 shadow-2xl backdrop-blur-md sm:rounded-[40px] sm:p-10 lg:p-12"
          style={{
            background: surface, // Keeping the passed gradient but using it subtly
            boxShadow: `0 25px 50px -12px ${accent}20` // Colored shadow
          }}
        >
          {/* Tagline Pill */}
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/40 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.2em] backdrop-blur-sm sm:mx-0 sm:text-[10px] sm:tracking-[0.25em]" style={{ color: accent }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }}></span>
            {heroTagline}
          </div>

          <h1 className="mt-6 text-center text-3xl font-bold leading-tight text-slate-900 sm:text-left sm:text-4xl lg:text-5xl">
            {title}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-center text-base leading-relaxed text-slate-700 sm:text-left sm:text-lg">
            {description}
          </p>

          {/* Stats Grid */}
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="group relative overflow-hidden rounded-2xl border border-white/50 bg-white/60 px-6 py-5 text-center shadow-sm transition-all hover:-translate-y-1 hover:bg-white/80 hover:shadow-md sm:text-left"
              >
                <div className="absolute top-0 left-0 h-1 w-full opacity-50" style={{ backgroundColor: accent }} />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 sm:tracking-[0.25em]">{stat.label}</p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {stat.value}
                </p>
                {stat.helper && <p className="mt-1 text-[10px] font-medium text-slate-500">{stat.helper}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* --- FUN ELEMENT (Gallery/Interactive) --- */}
        {funElement ? (
          <div className="mx-auto mt-12 overflow-hidden rounded-[28px] shadow-xl ring-1 ring-slate-900/5 sm:rounded-[32px]">
            {funElement}
          </div>
        ) : null}

        {/* --- FEATURES GRID --- */}
        <section className="mt-14 sm:mt-16">
          <div className="mb-8 flex items-center gap-4">
             <div className="h-px flex-1 bg-slate-200"></div>
             <h2 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 sm:text-sm sm:tracking-[0.3em]">The Upgrade Plan</h2>
             <div className="h-px flex-1 bg-slate-200"></div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {features.map((feature, i) => (
              <article
                key={feature.title}
                className="group relative rounded-[28px] border border-white bg-white/60 p-6 pt-10 text-center shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-xl sm:rounded-[32px] sm:p-8 sm:pt-8 sm:text-left"
              >
                {/* Number Badge */}
                <div className="absolute -top-3 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border-4 border-[#F9F9F7] text-lg font-bold text-white shadow-sm sm:left-auto sm:right-4 sm:h-12 sm:w-12 sm:translate-x-0 sm:text-xl" style={{ backgroundColor: accent }}>
                  {i + 1}
                </div>

                <h3 className="text-lg font-bold text-slate-900 sm:text-xl">{feature.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {feature.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* --- CTA SECTION --- */}
        <section 
          className="relative mt-16 overflow-hidden rounded-[28px] px-5 py-12 text-center shadow-2xl sm:mt-20 sm:rounded-[40px] sm:px-8 sm:py-16"
        >
          {/* Background for CTA */}
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          {/* Glow Effect */}
          <div 
             className="absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-[100px]"
             style={{ background: accent }}
          />

          <div className="relative z-10 max-w-2xl mx-auto">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/60 sm:text-xs sm:tracking-[0.4em]">Next Step</p>
            <h3 className="mt-4 text-2xl font-bold text-white sm:text-3xl lg:text-4xl">
              {ctaLine ?? 'Ready to add this to your plan?'}
            </h3>
            <p className="mt-5 text-base text-slate-300 sm:mt-6 sm:text-lg">
              We plug this into the Home Pros roadmap so scheduling and materials stay coordinated.
            </p>
            
            <div className="mt-8 flex justify-center">
              <Link
                href={ctaHref}
                className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-white px-6 py-3 text-center text-xs font-bold uppercase tracking-[0.18em] text-slate-900 shadow-xl transition-all hover:scale-105 hover:shadow-2xl sm:px-8 sm:py-4 sm:text-sm sm:tracking-[0.25em]"
              >
                <span className="relative z-10">Request Free Estimate</span>
                <span 
                  className="absolute inset-0 -translate-x-full opacity-20 transition-transform duration-500 group-hover:translate-x-0"
                  style={{ backgroundColor: accent }}
                />
              </Link>
            </div>
          </div>
        </section>
        
        {/* Footer Link */}
        <div className="mt-12 text-center">
           <Link href="/home-pros" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600">
             Home Pros for Texas © {new Date().getFullYear()}
           </Link>
        </div>

      </main>
    </div>
  );
}
